<template>
  <v-container fluid class="pa-4">
    <!-- Page header -->
    <div class="d-flex align-center mb-4">
      <v-icon large color="primary" class="mr-3">
        mdi-package-variant-closed
      </v-icon>
      <div>
        <div class="text-h5 font-weight-bold">
          Inventory Management
        </div>
        <div class="text-caption grey--text">
          Manage your stocks and measurement units
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <v-card elevation="2">
      <v-tabs v-model="activeTab" background-color="primary" dark slider-color="white">
        <v-tab>
          <v-icon left>
            mdi-cube-outline
          </v-icon>
          Stocks
        </v-tab>
        <v-tab>
          <v-icon left>
            mdi-ruler
          </v-icon>
          Units
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-items v-model="activeTab">
        <!-- ── Stocks Tab ── -->
        <v-tab-item>
          <v-card flat>
            <!-- Toolbar row -->
            <v-card-title class="d-flex align-center flex-wrap gap-2 py-3 px-4">
              <span class="text-subtitle-1 font-weight-medium">Stock List</span>
              <v-spacer />
              <excel-import />
              <v-btn
                v-if="authPermissions.has('stocks:create')"
                color="primary"
                class="ml-2"
                @click="createStockDialog = true"
              >
                <v-icon left>
                  mdi-plus
                </v-icon>
                Add Stock
              </v-btn>
            </v-card-title>
            <v-divider />
            <index-stock @editStock="initEditStock" />
          </v-card>
        </v-tab-item>

        <!-- ── Units Tab ── -->
        <v-tab-item>
          <v-card flat>
            <v-card-text class="pa-4">
              <v-row>
                <v-col
                  v-if="authPermissions.has('stock_units:create')"
                  cols="12"
                  md="4"
                >
                  <v-card outlined>
                    <v-card-title class="text-subtitle-1 font-weight-medium primary white--text py-2 px-4">
                      <v-icon left class="white--text">
                        mdi-plus-circle-outline
                      </v-icon>
                      Add Unit
                    </v-card-title>
                    <v-card-text class="pa-2">
                      <create-unit />
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col
                  v-if="authPermissions.has('stock_units:view')"
                  cols="12"
                  :md="authPermissions.has('stock_units:create') ? 8 : 12"
                >
                  <v-card outlined>
                    <v-card-title class="text-subtitle-1 font-weight-medium primary white--text py-2 px-4">
                      <v-icon left class="white--text">
                        mdi-format-list-bulleted
                      </v-icon>
                      Unit List
                    </v-card-title>
                    <index-unit @editUnit="initEditUnit" />
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Create Stock Dialog -->
    <v-dialog
      v-if="authPermissions.has('stocks:create')"
      v-model="createStockDialog"
      max-width="600px"
      style="-webkit-app-region: no-drag"
    >
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left class="white--text">
            mdi-plus
          </v-icon>
          Add Stock
          <v-spacer />
          <v-btn icon dark @click="createStockDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <create-stock @close-dialog="createStockDialog = false" />
      </v-card>
    </v-dialog>

    <!-- Edit Stock Dialog -->
    <v-dialog v-model="editStockDialog" dismissable max-width="600px" eager style="-webkit-app-region: no-drag">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left class="white--text">
            mdi-pencil
          </v-icon>
          Edit Stock
          <v-spacer />
          <v-btn icon dark @click="editStockDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <edit-stock ref="edit-stock" @close-dialog="editStockDialog = false" />
      </v-card>
    </v-dialog>

    <!-- Edit Unit Dialog -->
    <v-dialog v-model="editUnitDialog" dismissable max-width="500px" eager style="-webkit-app-region: no-drag">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left class="white--text">
            mdi-pencil
          </v-icon>
          Edit Unit
          <v-spacer />
          <v-btn icon dark @click="editUnitDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <edit-unit
          :prop-unit-id="propUnitData.id"
          :prop-unit-data="propUnitData"
          @close-dialog="editUnitDialog = false"
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'VStocks',
  components: {
    createStock: () => import('./../components/stock/Create'),
    indexStock: () => import('./../components/stock/Index'),
    editStock: () => import('./../components/stock/Edit'),
    createUnit: () => import('./../components/unit/Create'),
    indexUnit: () => import('./../components/unit/Index'),
    editUnit: () => import('./../components/unit/Edit'),
    excelImport: () => import('@/views/dashboard/components/stock/Import'),
  },
  data() {
    return {
      activeTab: 0,
      createStockDialog: false,
      editStockDialog: false,
      editUnitDialog: false,
      propUnitData: {
        unit: null,
        description: null,
      },
    }
  },
  computed: {
    ...mapState({
      unitUpdated: state => state.unit.channelUpdate,
      authPermissions: state => state.auth.userPermissions,
    }),
  },
  watch: {
    unitUpdated() {
      this.fetchAllUnits()
    },
  },
  mounted() {
    this.fetchAllUnits()
  },
  methods: {
    ...mapActions({
      fetchAllUnits: 'unit/fetchAll',
    }),
    initEditUnit(unitData) {
      this.editUnitDialog = true
      this.propUnitData = unitData
    },
    initEditStock(stockData) {
      const { populateInput } = this.$refs['edit-stock']
      this.editStockDialog = true
      populateInput(stockData)
    },
  },
}
</script>
